// Exporting a list as it's shown on screen (#9), as Excel, CSV or JSON. Done in
// the browser: the screen already holds the rows, filtered as the person sees
// them, so the file is ready at once with no job to wait for.

export type ExportFormat = 'xlsx' | 'csv' | 'json';

export type ExportColumn<T> = {
  header: string;
  /** Key in the JSON file; snake_case of the header when left out. */
  key?: string;
  kind?: 'text' | 'money' | 'date' | 'number';
  value: (row: T) => string | number | null | undefined;
};

export const FORMATS: { key: ExportFormat; label: string; ext: string; mime: string }[] = [
  { key: 'xlsx', label: 'Excel (.xlsx)', ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  { key: 'csv', label: 'CSV (.csv)', ext: 'csv', mime: 'text/csv;charset=utf-8' },
  { key: 'json', label: 'JSON (.json)', ext: 'json', mime: 'application/json' },
];

const keyOf = <T,>(c: ExportColumn<T>) =>
  c.key ?? c.header.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/** Money arrives as the API's string ("1234.5000"); a number in the file, to the cent. */
function cell<T>(c: ExportColumn<T>, row: T): string | number | null {
  const v = c.value(row);
  if (v === null || v === undefined || v === '') return null;
  if (c.kind === 'money' || c.kind === 'number') {
    const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, '').replace(/−/g, '-'));
    if (!Number.isFinite(n)) return String(v);
    return c.kind === 'money' ? Math.round(n * 100) / 100 : n;
  }
  return typeof v === 'number' ? v : String(v);
}

export function toCsv<T>(columns: ExportColumn<T>[], rows: T[]): string {
  const esc = (v: string | number | null) => {
    if (v === null) return '';
    const s = typeof v === 'number' ? (Number.isInteger(v) ? String(v) : v.toFixed(2)) : v;
    // Quote when needed; a leading = + - @ is prefixed so a spreadsheet won't run it as a formula.
    const safe = typeof v === 'string' && /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const lines = [columns.map((c) => esc(c.header)).join(',')];
  for (const r of rows) lines.push(columns.map((c) => esc(cell(c, r))).join(','));
  return '﻿' + lines.join('\r\n') + '\r\n'; // BOM so Excel reads € and accents
}

export function toJson<T>(columns: ExportColumn<T>[], rows: T[]): string {
  return JSON.stringify(rows.map((r) => Object.fromEntries(columns.map((c) => [keyOf(c), cell(c, r)]))), null, 2);
}

// ---- .xlsx: a zip of a few XML files ------------------------------------------------------------------

const xml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  // Characters XML 1.0 doesn't allow.
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

function colName(i: number): string {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** Days since 1899-12-30, as Excel counts dates. */
function excelDate(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return (Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!) - Date.UTC(1899, 11, 30)) / 86_400_000;
}

// Styles: 0 normal, 1 bold header, 2 date, 3 money.
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2"><numFmt numFmtId="164" formatCode="dd/mm/yyyy"/><numFmt numFmtId="165" formatCode="#,##0.00"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="4"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

function sheetXml<T>(columns: ExportColumn<T>[], rows: T[]): string {
  const out: string[] = [];
  const widths = columns.map((c) => Math.max(c.header.length, 8));
  const row = (r: number, cells: string[]) => out.push(`<row r="${r}">${cells.join('')}</row>`);
  row(1, columns.map((c, i) => `<c r="${colName(i)}1" t="inlineStr" s="1"><is><t>${xml(c.header)}</t></is></c>`));
  rows.forEach((data, ri) => {
    const r = ri + 2;
    row(r, columns.map((c, i) => {
      const ref = `${colName(i)}${r}`;
      const v = cell(c, data);
      if (v === null) return '';
      if (c.kind === 'date' && typeof v === 'string') {
        const d = excelDate(v);
        if (d !== null) { widths[i] = Math.max(widths[i]!, 10); return `<c r="${ref}" s="2"><v>${d}</v></c>`; }
      }
      if (typeof v === 'number') {
        widths[i] = Math.max(widths[i]!, String(v).length + 2);
        return `<c r="${ref}"${c.kind === 'money' ? ' s="3"' : ''}><v>${v}</v></c>`;
      }
      widths[i] = Math.max(widths[i]!, Math.min(v.length, 60));
      return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xml(v)}</t></is></c>`;
    }));
  });
  const cols = widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w + 2}" customWidth="1"/>`).join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><cols>${cols}</cols><sheetData>${out.join('')}</sheetData></worksheet>`;
}

export function toXlsx<T>(columns: ExportColumn<T>[], rows: T[], sheetName = 'Sheet1'): Uint8Array {
  const name = xml(sheetName.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Sheet1');
  return zip([
    ['[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`],
    ['_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`],
    ['xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${name}" sheetId="1" r:id="rId1"/></sheets></workbook>`],
    ['xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`],
    ['xl/styles.xml', STYLES],
    ['xl/worksheets/sheet1.xml', sheetXml(columns, rows)],
  ]);
}

// A zip with every file stored (not compressed): simple, and Excel opens it.
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(b: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < b.length; i++) c = CRC_TABLE[(c ^ b[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function zip(files: [string, string][]): Uint8Array {
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const [path, text] of files) {
    const name = enc.encode(path);
    const data = enc.encode(text);
    const crc = crc32(data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); local.setUint16(4, 20, true); local.setUint16(6, 0x0800, true); // UTF-8 names
    local.setUint16(8, 0, true); local.setUint16(10, 0, true); local.setUint16(12, 0x21, true); // stored; 1980-01-01
    local.setUint32(14, crc, true); local.setUint32(18, data.length, true); local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true); local.setUint16(28, 0, true);
    parts.push(new Uint8Array(local.buffer), name, data);
    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true); cd.setUint16(4, 20, true); cd.setUint16(6, 20, true); cd.setUint16(8, 0x0800, true);
    cd.setUint16(10, 0, true); cd.setUint16(12, 0, true); cd.setUint16(14, 0x21, true);
    cd.setUint32(16, crc, true); cd.setUint32(20, data.length, true); cd.setUint32(24, data.length, true);
    cd.setUint16(28, name.length, true); cd.setUint32(42, offset, true);
    central.push(new Uint8Array(cd.buffer), name);
    offset += 30 + name.length + data.length;
  }
  const cdSize = central.reduce((n, p) => n + p.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
  end.setUint32(12, cdSize, true); end.setUint32(16, offset, true);
  const all = [...parts, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of all) { out.set(p, at); at += p.length; }
  return out;
}

// ---- the file ------------------------------------------------------------------------------------------

export function exportBlob<T>(format: ExportFormat, columns: ExportColumn<T>[], rows: T[], sheetName: string): Blob {
  const f = FORMATS.find((x) => x.key === format)!;
  if (format === 'xlsx') return new Blob([toXlsx(columns, rows, sheetName)], { type: f.mime });
  return new Blob([format === 'csv' ? toCsv(columns, rows) : toJson(columns, rows)], { type: f.mime });
}

/** "invoices-2026-10-18.xlsx" */
export const exportFileName = (base: string, format: ExportFormat, today: string) =>
  `${base.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${today}.${FORMATS.find((f) => f.key === format)!.ext}`;

export function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
